import type { ResourceTableDescriptor, TranslateFn } from '@/components/ui/embedded-resource-table/types';
import type { ToolbarFilterOption } from '@/components/ui/generic-toolbar/GenericToolbar';
import { buildAssignedUserOptions, type AssignableUser } from '@/lib/assigned-user-options';
import { PROJECT_COUNTRY_TYPE_OPTIONS } from '@/lib/constants';
import { settingLabel } from '@/lib/status-labels';
import type { Project } from '@/types';

/** Sentinel the backend understands as "the user explicitly cleared every year — show all projects". */
const TIMELINE_YEAR_ALL = 'all';

/**
 * Deserialize the `timeline_year` filter the server echoes back into the list of selected years.
 * - absent/empty → the current year (the default the backend also applies on a fresh load)
 * - the "all" sentinel → an empty list (no year filter, i.e. every project)
 * - otherwise → the comma-separated years, e.g. "2026,2025"
 */
function parseTimelineYear(value: string | number | null | undefined, currentYear: string): string[] {
    if (value === undefined || value === null || value === '') {
        return [currentYear];
    }

    const raw = String(value);
    if (raw === TIMELINE_YEAR_ALL) {
        return [];
    }

    return raw
        .split(',')
        .map((year) => year.trim())
        .filter(Boolean);
}

/** Serialize the selected years into the scalar `timeline_year` value the server expects. */
function serializeTimelineYear(years: string[] | undefined): string {
    return years && years.length > 0 ? years.join(',') : TIMELINE_YEAR_ALL;
}

/** Everyone on a project, in the order the Assigned column stacks them. */
function assignedIds(project: Project): string[] {
    return [project.owner?.id, project.project_manager?.id, project.project_engineer?.id].filter((id): id is string => Boolean(id));
}

function timelineYears(project: Project): string[] {
    // Matches the server, which keeps a project whose start *or* end falls in the year.
    return [project.start_date, project.end_date]
        .filter(Boolean)
        .map((date) => String(new Date(date as string).getFullYear()))
        .filter((year) => year !== 'NaN');
}

/**
 * The one description of the projects table, read by `pages/projects/index.tsx`,
 * by every detail-page tab that lists projects, and by ProjectTable itself.
 */
export const projectTableDescriptor: ResourceTableDescriptor<Project> = {
    key: 'projects',
    resourceName: 'projects',
    resourceNamePlural: 'projects',
    getRowId: (project) => project.id,

    columns: [
        {
            id: 'name',
            labelKey: 'projects.tables.project',
            labelFallback: 'Project',
            sort: { serverKey: 'contract_number', value: (project) => project.contract_number ?? '' },
        },
        {
            id: 'contract_name',
            labelKey: 'projects.tables.contract_name',
            labelFallback: 'Contract name',
            sort: { serverKey: 'contract_name', value: (project) => project.contract_name ?? '' },
        },
        {
            id: 'company',
            labelKey: 'projects.tables.company',
            labelFallback: 'Company',
            sort: { serverKey: 'company_name', value: (project) => project.company?.name ?? '' },
        },
        {
            id: 'created_by',
            labelKey: 'projects.tables.created_by',
            labelFallback: 'Created by',
        },
        {
            id: 'status',
            labelKey: 'projects.tables.status',
            labelFallback: 'Status',
            sort: { serverKey: 'status', value: (project) => String(project.status) },
        },
        {
            id: 'country_type',
            labelKey: 'projects.tables.country_type',
            labelFallback: 'Region',
        },
        {
            id: 'start_date',
            labelKey: 'projects.tables.start_date',
            labelFallback: 'Start Date',
            sort: { serverKey: 'start_date', value: (project) => project.start_date ?? '' },
        },
        {
            id: 'address_street',
            labelKey: 'projects.tables.address_street',
            labelFallback: 'Address Street',
        },
        {
            id: 'sales_price',
            labelKey: 'projects.tables.sales_price',
            labelFallback: 'Sales Price',
            sort: { serverKey: 'sales_price', value: (project) => Number(project.sales_price ?? 0) },
        },
        // Deliberately last, and deliberately one column: the people on a project
        // read as a group, and a row of avatars takes less room than three name
        // columns. No sort — there is no sensible ordering over three people, and
        // offering one the server silently drops is worse than offering none.
        {
            id: 'assigned',
            labelKey: 'projects.tables.assigned',
            labelFallback: 'Assigned',
            compact: true,
        },
    ],

    defaultVisibleColumns: ['name', 'contract_name', 'company', 'status', 'start_date', 'address_street', 'sales_price', 'assigned'],

    filters: [
        {
            // First card: the year is what people narrow by before anything else.
            id: 'timeline_year',
            param: 'timeline_year',
            labelKey: 'projects.index.filters.timeline_year',
            labelFallback: 'Project year',
            // Serialised as a comma-joined string with an explicit "all" sentinel, which
            // is what distinguishes "the user cleared every year" from "no year param at
            // all" — the latter defaults to the current year on the server.
            toQuery: (values) => serializeTimelineYear(values),
            fromQuery: (serverFilters) =>
                parseTimelineYear(serverFilters.timeline_year as string | number | null | undefined, new Date().getFullYear().toString()),
            defaultValues: [new Date().getFullYear().toString()],
            matches: (project, values) => timelineYears(project).some((year) => values.includes(year)),
        },
        {
            id: 'status',
            param: 'status',
            labelKey: 'projects.index.quick_filters.status',
            labelFallback: 'Status',
            matches: (project, values) => values.includes(String(project.status)),
        },
        {
            id: 'country_type',
            param: 'country_type',
            labelKey: 'projects.tables.country_type',
            labelFallback: 'Region',
            matches: (project, values) => values.includes(String(project.country_type ?? '')),
        },
        {
            id: 'company',
            param: 'company_id',
            labelKey: 'projects.tables.company',
            labelFallback: 'Company',
            matches: (project, values) => values.includes(project.company?.id ?? project.company_id ?? ''),
        },
        {
            // `owner_id` is historical: the server matches it against the owner, the
            // project manager and the engineer alike, so the label names the
            // seat-agnostic question it actually asks.
            id: 'owner',
            param: 'owner_id',
            labelKey: 'projects.index.filters.assigned_person',
            labelFallback: 'Assigned person',
            matches: (project, values) => assignedIds(project).some((id) => values.includes(id)),
        },
        {
            id: 'created_by',
            param: 'created_by',
            labelKey: 'projects.tables.created_by',
            labelFallback: 'Created by',
            matches: (project, values) => values.includes(String(project.created_by ?? '')),
        },
    ],

    // Project year first, then status, region, company, people.
    quickFilterIds: ['timeline_year', 'status', 'country_type', 'company', 'owner', 'created_by'],
    // Quick filters are the only filters here: the year and region cards would
    // otherwise appear twice, once as a quick filter and once as a column.
    showMoreFilters: false,
    excludeFromMoreFilters: ['status', 'timeline_year'],

    searchValues: (project) => [project.contract_number, project.contract_name, project.company?.name, project.address_street],

    allLabel: { labelKey: 'projects.index.filters.all_projects', labelFallback: 'All Projects' },
    quickFiltersLabel: { labelKey: 'projects.index.toolbar.quick_filters', labelFallback: 'Quick filters' },
};

export interface ProjectFilterOptionSources {
    t: TranslateFn;
    statuses: Array<{ value: string; label?: string }>;
    users: AssignableUser[];
    companies: Array<{ id: string; name: string }>;
    years?: Array<string | number>;
}

export function projectFilterOptions({
    t,
    statuses,
    users,
    companies,
    years = [],
}: ProjectFilterOptionSources): Record<string, ToolbarFilterOption[]> {
    const assignedOptions = buildAssignedUserOptions(users, t);

    return {
        status: statuses.map((status) => ({
            value: status.value,
            label: t(`projects.index.statuses.${status.value}`, status.label ?? status.value),
        })),
        // Region labels used to come straight from config.json, which is English
        // only — so a Lithuanian user saw "Local"/"Export" among translated cards.
        country_type: PROJECT_COUNTRY_TYPE_OPTIONS.map((option) => ({
            value: option.value,
            label: settingLabel(t, 'project_country_types', option.value) || option.label,
        })),
        company: companies.map((company) => ({ value: company.id, label: company.name })).sort((a, b) => a.label.localeCompare(b.label)),
        owner: assignedOptions,
        created_by: assignedOptions,
        timeline_year: years.map((year) => ({ value: String(year), label: String(year) })),
    };
}

/** Fallback sources for a tab handed rows but no reference lists. */
export function projectOptionSourcesFromRows(projects: Project[]): Pick<ProjectFilterOptionSources, 'users' | 'companies' | 'years'> {
    const users = new Map<string, AssignableUser>();
    const companies = new Map<string, { id: string; name: string }>();
    const years = new Set<string>();

    projects.forEach((project) => {
        [project.owner, project.project_manager, project.project_engineer].forEach((person) => {
            if (person?.id) {
                users.set(person.id, person as AssignableUser);
            }
        });
        if (project.company?.id && project.company?.name) {
            companies.set(project.company.id, { id: project.company.id, name: project.company.name });
        }
        timelineYears(project).forEach((year) => years.add(year));
    });

    return {
        users: [...users.values()],
        companies: [...companies.values()],
        years: [...years].sort((a, b) => Number(b) - Number(a)),
    };
}
